import { BlockType, useEditorUI } from "@pathwright/blocks-engine"
import styled from "styled-components"
import { BLOCK_HASH_PREFIX } from "../blocksLinks/constants"
import { defaultBlockStyles } from "../editor/BlockStyles/base-style-defaults"

const StyledBlockWrapper = styled.div<{
  $styledColor: string
  $styledBackground?: string
  $editing?: boolean
}>`
  padding: ${defaultBlockStyles.styleAttributes.paddingTop}
    ${defaultBlockStyles.styleAttributes.paddingRight};
  position: relative;
  transition: all 0.3s;

  &:focus-visible {
    outline: none;
  }

  .ql-bubble .ql-editor {
    h1 {
      font-size: 42px;
    }

    h2 {
      font-size: 36px;
    }

    h3 {
      font-size: 32px;
    }
  }

  .ContentText .ql-editor {
    padding: 0px;
    overflow: visible;

    /* Solves Safari printing bug */
    @media print {
      & {
        height: auto !important;
      }
    }
  }

  @media (max-width: 768px) {
    padding: 15px 0px;
    padding-top: ${(props) => (props.$editing ? 20 : 15)}px;
  }

  // Apply block styles text colors
  ${(p) =>
    p.$styledColor
      ? `
    .ql-editor,
    .ql-editor > *,
    .ql-editor.ql-blank::before,
    .SubText,
    .SubText > *,
    .BlockText,
    .BlockText > *:not(pre),
    .ContentText .ql-blank:before,
    .MatchingMatchInput,
    .MatchingMatchInput::placeholder {
      color: ${p.$styledColor};
      transition: color 0.5s;
    }

    --styled-block-color: ${p.$styledColor};

    .ql-editor.ql-blank::before,
    .MatchingMatchInput::placeholder {
      opacity: 0.6;
    }
  `
      : ""}

  ${(p) =>
    p.$styledBackground
      ? `
    --styled-block-background: ${p.$styledBackground};
  `
      : ""}

  .BlockValidationIndicator {
    position: absolute;
    top: 40px;
    left: 10px;

    @media (min-width: 769px) {
      left: 20px;
    }
  }

  @keyframes highlight {
    from {
      background-color: rgba(255, 255, 0, 0.5);
    }
    to {
      background-color: transparent;
    }
  }

  &:hover .block-link-icon {
    opacity: 1;
  }

  // uncomment the styles below for testing currently selected Blocks via the URL hash
  &.is-targeted-in-hash::after {
    // content: "";
    // position: absolute;
    // top: 0;
    // left: 0;
    // right: 0;
    // bottom: 0;
    // width: 100%;
    // height: 100%;
    // background-color: rgba(255, 255, 0, 0.5);
  }

  @media print {
    & {
      height: max-content;
    }
    &.smooth-scroll-active {
      display: none;
    }
  }
`

type EditorBlockWrapProps = {
  children: React.ReactNode
  styledColor: string
  style: React.CSSProperties
  styledBackground?: string
  className?: string
  index?: number
}
export const EditorBlockWrap = ({
  children,
  className = "",
  styledColor,
  styledBackground,
  index,
  style
}: EditorBlockWrapProps) => {
  const { setFocusedBlockIndex } = useEditorUI()

  const handleFocus = () => {
    if (!index) return
    setFocusedBlockIndex?.(index)
  }

  const handleBlur = () => {
    setFocusedBlockIndex?.(null)
  }

  return (
    <StyledBlockWrapper
      $styledColor={styledColor}
      $styledBackground={styledBackground}
      className={`BlockWrap ${className}`}
      style={style}
      $editing={true}
      onBlur={handleBlur}
      onFocus={handleFocus}
    >
      {children}
    </StyledBlockWrapper>
  )
}

type BlockWrapProps = {
  children: React.ReactNode
  style: React.CSSProperties
  block: BlockType
  styledColor: string
  styledBackground?: string
  className?: string
}

// View mode only
const BlockWrap = ({
  children,
  className = "",
  styledColor,
  styledBackground,
  style,
  block
}: BlockWrapProps) => {
  return (
    <StyledBlockWrapper
      $styledColor={styledColor}
      $styledBackground={styledBackground}
      className={`BlockWrap ${className} block-type-${block.type}-${block.layout}`}
      style={style}
      id={`${BLOCK_HASH_PREFIX}${block.id}`}
    >
      {children}
    </StyledBlockWrapper>
  )
}

export default BlockWrap
